import { useFeatureRestrictionStore } from '@/modules/auth/stores/FeatureRestrictionStore'
import { ContainerType, ElementType, RootEntityType } from '@/modules/elements/models/Element'
import { EmbedType } from '@/modules/elements/models/Embed'
import { FeatureUpgrade } from '@/modules/featurerestriction/models/FeatureUpgrade'
import { useUpgradeFeatureModalStore } from '@/modules/global/stores/UpgradeFeatureModalStore'
import i18n from '@/util/config/i18n'
import { type ChainedCommands, Extension, generateHTML } from '@tiptap/core'
import Suggestion from '@tiptap/suggestion'
import { Editor } from '@tiptap/vue-2'
import VueI18n from 'vue-i18n'
import type { EditorContext } from './tiptap-editor-context'
import { FocusPosition } from './reorder-util'

export type SlashCommandScope = 'content' | 'element'

export type CommandProps = {
  editor: Editor
  range: any
  parent: Vue | null
  addElement: (...data: AddElementData[]) => void
  replaceElement: (data: AddElementData) => void
  boundsFunction: Function
  slashScope?: EditorContext['slashScope']
}

export type SlashCommand = {
  key: string
  scope: SlashCommandScope
  index?: number
  category: string
  title: string
  subTitle: string
  icon: string
  command: (props: CommandProps) => void
  keywords?: string[]
}

export function shouldApplySlashInEditor(slashScope: EditorContext['slashScope'], editor: Editor): boolean {
  if (slashScope === 'content') return true
  return editor.state.doc.textContent.startsWith('/')
}

export type SlashExecutionContext = Pick<
  EditorContext,
  'slashScope' | 'addElementHandler' | 'replaceElementHandler' | 'slashCommands'
>

export type SlashCommandBlockReason = 'scope' | 'handler' | 'disabled' | 'feature' | 'stale'

export type SlashCommandEligibilityItem = Pick<SlashCommand, 'key' | 'scope'>

export function canExecuteSlashCommand(
  item: SlashCommandEligibilityItem,
  ctx: SlashExecutionContext,
  options?: { enforceElementHandler?: boolean }
): { ok: true } | { ok: false; reason: SlashCommandBlockReason } {
  if (!ctx.slashCommands) {
    return { ok: false, reason: 'disabled' }
  }
  if (ctx.slashScope === 'content' && item.scope !== 'content') {
    return { ok: false, reason: 'scope' }
  }
  const enforceElementHandler = options?.enforceElementHandler !== false
  if (enforceElementHandler && item.scope === 'element' && !ctx.addElementHandler) {
    return { ok: false, reason: 'handler' }
  }
  if (item.key === 'signature' && !useFeatureRestrictionStore().allowSignature) {
    return { ok: false, reason: 'feature' }
  }
  return { ok: true }
}

export type AddElementData = {
  type: ElementType
  containerType?: ContainerType
  embedType?: EmbedType
  textContent?: string
  embedUrl?: string
  embedHtml?: string
  welcomeElementPayload?: WelcomeElementPayload
  focus?: FocusPosition
  tableRows?: number
  tableColumns?: number
  tablePasteGrid?: string[][]
}

export type WelcomeElementPayload = {
  welcomeText?: string
  profileText?: string
}

export default Extension.create({
  name: 'slashcommand',

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...(this.options as any).suggestion
      })
    ]
  }
})

/** `title` and `subTitle` default to the `<key>Title` / `<key>Subtitle` translations. */
type SlashCommandDefinition = Omit<SlashCommand, 'title' | 'subTitle'> & { title?: string; subTitle?: string }

type SlashCommandGetter = (t: typeof VueI18n.prototype.t) => SlashCommandDefinition[]

/**
 * A text-block command: inside a content editor it turns the typed line into the block, anywhere else it adds the
 * block as a message element.
 */
function slashRangeText(editor: Editor, range: { from: number; to: number }): string {
  const doc = editor.state.doc
  const from = Math.max(0, Math.min(range.from, doc.content.size))
  const to = Math.max(from, Math.min(range.to, doc.content.size))
  return doc.textBetween(from, to, '')
}

function blockCommand(apply: (chain: ChainedCommands) => ChainedCommands, html: string) {
  return ({ editor, range, addElement, slashScope }: CommandProps) => {
    // Apply in the current editor when this suggestion still contains "/", including a slash that is not
    // the first character. Content scope always applies in the editor.
    const applyInEditor = slashRangeText(editor, range).startsWith('/') || shouldApplySlashInEditor(slashScope, editor)
    const chain = editor.chain().focus().deleteRange(range)
    if (applyInEditor) {
      apply(chain).focus().run()
    } else {
      chain.run()
      addElement({ type: ElementType.message, textContent: html })
    }
  }
}

const textCommands: SlashCommandGetter = t => {
  return [
    {
      key: 'heading1',
      scope: 'content',
      category: t('text'),
      icon: 'header-1',
      keywords: ['h1'],
      command: blockCommand(chain => chain.setNode('heading', { level: 1 }), '<h1></h1>')
    },
    {
      key: 'heading2',
      scope: 'content',
      category: t('text'),
      icon: 'header-2',
      keywords: ['h2'],
      command: blockCommand(chain => chain.setNode('heading', { level: 2 }), '<h2></h2>')
    },
    {
      key: 'heading3',
      scope: 'content',
      category: t('text'),
      icon: 'header-3',
      keywords: ['h3'],
      command: blockCommand(chain => chain.setNode('heading', { level: 3 }), '<h3></h3>')
    },
    {
      key: 'paragraph',
      scope: 'content',
      category: t('text'),
      icon: 'menu-alt-2',
      keywords: ['t', 'te', 'tex', 'text'],
      command: blockCommand(chain => chain.setNode('paragraph'), '<p></p>')
    },
    {
      key: 'paragraphSmall',
      scope: 'content',
      category: t('text'),
      icon: 'menu-alt-2',
      keywords: ['t', 'te', 'tex', 'text'],
      command: blockCommand(
        chain => chain.setNode('paragraph').updateAttributes('paragraph', { class: 'small' }),
        '<p class="small"></p>'
      )
    },
    {
      key: 'bulletList',
      scope: 'content',
      category: t('text'),
      icon: 'bullet-list',
      command: blockCommand(chain => chain.toggleBulletList(), '<ul><li></li></ul>')
    },
    {
      key: 'checklist',
      scope: 'content',
      category: t('text'),
      title: t('checkBoxListTitle'),
      subTitle: t('checkBoxListSubtitle'),
      icon: 'checklist',
      command: blockCommand(chain => chain.toggleTaskList(), '<ul data-type="taskList"><li></li></ul>')
    },
    {
      key: 'orderedList',
      scope: 'content',
      category: t('text'),
      icon: 'ordered-list',
      command: blockCommand(chain => chain.toggleOrderedList(), '<ol><li></li></ol>')
    },
    {
      key: 'letteredList',
      scope: 'content',
      category: t('text'),
      icon: 'ordered-list',
      keywords: ['l', 'le', 'let', 'letter', 'lettered'],
      command: blockCommand(chain => chain.toggleLetteredList(), '<ol type="a"><li></li></ol>')
    },
    {
      key: 'mention',
      scope: 'content',
      category: t('text'),
      icon: 'at-symbol',
      command: ({ editor, range }: CommandProps) => {
        editor!.chain().focus().deleteRange(range).insertContent('@').run()
      }
    },
    {
      key: 'blockquote',
      scope: 'content',
      category: t('text'),
      icon: 'quote',
      command: ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).toggleBlockquote().focus().run()
      }
    },
    {
      key: 'horizontalRule',
      scope: 'content',
      category: t('layout'),
      title: t('dividerTitle'),
      subTitle: t('dividerSubtitle'),
      icon: 'minus',
      command: ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).setHorizontalRule().focus().run()
      }
    }
  ] as SlashCommandDefinition[]
}

const proposalCommands: SlashCommandGetter = t => {
  return [
    {
      key: 'welcome',
      scope: 'element',
      category: t('proposal'),
      icon: 'hand',
      command: (props: CommandProps) => {
        const elementData = {
          type: ElementType.welcome,
          welcomeElementPayload: {
            welcomeText: i18n.t('welcomeElement.prefilledWelcomeText') as string,
            profileText: i18n.t('welcomeElement.prefilledProfileText') as string
          }
        }
        replaceOrAddElement(props, elementData)
      }
    },
    {
      key: 'summary',
      scope: 'element',
      category: t('proposal'),
      icon: 'chat',
      command: (props: CommandProps) => {
        const elementData = { type: ElementType.summary, textContent: i18n.t('summary.prefilledText1') as string }
        replaceOrAddElement(props, elementData)
      }
    },
    {
      key: 'proposition',
      scope: 'element',
      category: t('proposal'),
      icon: 'star',
      keywords: ['vp'],
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.proposition })
      }
    },
    {
      key: 'teamOverview',
      scope: 'element',
      category: t('proposal'),
      icon: 'users',
      keywords: ['teams', 'overview', 'team'],
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.teamoverview })
      }
    },
    {
      key: 'contactCard',
      scope: 'element',
      category: t('proposal'),
      title: t('contactCard'),
      subTitle: t('contactCardSubtitle'),
      icon: 'identification',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.contactcard })
      }
    },
    {
      key: 'clientLogos',
      scope: 'element',
      category: t('proposal'),
      icon: 'office-building',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.clientlogos })
      }
    },
    {
      key: 'faq',
      scope: 'element',
      category: t('proposal'),
      icon: 'question-mark-circle',
      keywords: ['faq', 'fa', 'qna', 'question', 'answer', 'q&a'],
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.faq })
      }
    },
    {
      key: 'ctaBox',
      scope: 'element',
      category: t('proposal'),
      icon: 'cursor-click',
      keywords: ['cta'],
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.ctabox })
      }
    }
  ] as SlashCommandDefinition[]
}

const progressCommands: SlashCommandGetter = t => {
  return [
    {
      key: 'timeline',
      scope: 'element',
      category: t('progress'),
      icon: 'calendar',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.timeline })
      }
    },
    {
      key: 'taskList',
      scope: 'element',
      category: t('progress'),
      icon: 'clipboard-list',
      keywords: ['task', 'list', 'tasklist'],
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.tasklist })
      }
    }
  ] as SlashCommandDefinition[]
}

const offerCommands: SlashCommandGetter = t => {
  return [
    {
      key: 'offerHeader',
      scope: 'element',
      category: t('offer'),
      icon: 'printer',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.offerheader })
      }
    },
    {
      key: 'offer',
      scope: 'element',
      category: t('offer'),
      icon: 'cash',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.offer })
      }
    },
    {
      key: 'signature',
      scope: 'element',
      category: t('offer'),
      icon: 'pencil',
      command: (props: CommandProps) => {
        if (!useFeatureRestrictionStore().allowSignature) {
          props.editor!.chain().focus().deleteRange(props.range).run()
          useUpgradeFeatureModalStore().showUpgradeFeatureModal(FeatureUpgrade.SIGNATURE)
        } else {
          replaceOrAddElement(props, { type: ElementType.signature })
        }
      }
    }
  ] as SlashCommandDefinition[]
}

const mediaCommands: SlashCommandGetter = t => {
  return [
    {
      key: 'file',
      scope: 'element',
      category: t('media'),
      icon: 'document-text',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.FILE })
      }
    },
    {
      key: 'image',
      scope: 'element',
      category: t('media'),
      icon: 'photograph',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.IMAGE })
      }
    },
    {
      key: 'video',
      scope: 'element',
      category: t('media'),
      icon: 'video-camera',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.VIDEO })
      }
    },
    {
      key: 'pdf',
      scope: 'element',
      category: t('media'),
      icon: 'document',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.PDF })
      }
    },
    {
      key: 'bookmark',
      scope: 'element',
      category: t('media'),
      icon: 'bookmark',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.linkbookmark })
      }
    }
  ] as SlashCommandDefinition[]
}

const fileManagementCommands: SlashCommandGetter = t => {
  return [
    {
      key: 'resourceList',
      scope: 'element',
      category: t('fileManagement'),
      icon: 'folder',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.resourcelist })
      }
    },
    {
      key: 'clientResourceList',
      scope: 'element',
      category: t('fileManagement'),
      icon: 'folder-add',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.clientresources })
      }
    }
  ] as SlashCommandDefinition[]
}

const layoutCommands: SlashCommandGetter = t => {
  return [
    {
      key: 'divider',
      scope: 'element',
      category: t('layout'),
      icon: 'minus',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.divider })
      }
    },
    {
      key: 'table',
      scope: 'element',
      category: t('layout'),
      icon: 'table',
      command: (props: CommandProps) => {
        import('@/modules/elements/stores/TableSlashPickerStore').then(({ useTableSlashPickerStore }) => {
          useTableSlashPickerStore().open(props)
        })
      }
    },
    {
      key: 'accordion',
      scope: 'element',
      category: t('layout'),
      icon: 'chevron-down',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.container, containerType: ContainerType.TOGGLE })
      }
    }
  ] as SlashCommandDefinition[]
}

const embedCommands: SlashCommandGetter = t => {
  return [
    {
      key: 'genericEmbed',
      scope: 'element',
      category: t('embeds'),
      icon: 'puzzle',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.GENERIC })
      }
    },
    {
      key: 'googleDrive',
      scope: 'element',
      category: t('embeds'),
      icon: 'google-drive',
      keywords: ['gd'],
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.GDOCS })
      }
    },
    {
      key: 'youtube',
      scope: 'element',
      category: t('embeds'),
      title: t('youtubeEmbedHeadline'),
      subTitle: t('youtubeEmbedSubtitle'),
      icon: 'youtube',
      keywords: ['yt'],
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.YOUTUBE })
      }
    },
    {
      key: 'pitch',
      scope: 'element',
      category: t('embeds'),
      title: t('pitchEmbedTitle'),
      subTitle: t('pitchEmbedSubtitle'),
      icon: 'pitch',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.PITCH })
      }
    },
    {
      key: 'loom',
      scope: 'element',
      category: t('embeds'),
      title: t('loomEmbedHeadline'),
      subTitle: t('loomEmbedSubtitle'),
      icon: 'loom',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.LOOM })
      }
    },
    {
      key: 'calendly',
      scope: 'element',
      category: t('embeds'),
      title: t('calendlyEmbedHeadline'),
      subTitle: t('calendlyEmbedSubtitle'),
      icon: 'calendly',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.CALENDLY })
      }
    },
    {
      key: 'typeform',
      scope: 'element',
      category: t('embeds'),
      title: t('typeformEmbedTitle'),
      subTitle: t('typeformEmbedSubtitle'),
      icon: 'typeform',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.TYPEFORM })
      }
    },
    {
      key: 'canva',
      scope: 'element',
      category: t('embeds'),
      title: t('canvaEmbedTitle'),
      subTitle: t('canvaEmbedSubtitle'),
      icon: 'canva',
      command: (props: CommandProps) => {
        replaceOrAddElement(props, { type: ElementType.embed, embedType: EmbedType.CANVA })
      }
    }
  ] as SlashCommandDefinition[]
}
/* Adding a new section to show the Variables in the slash commands
modified by Jofin on 09-12-2025 */
const variableCommands: SlashCommandGetter = t => {
  return [
    {
      key: 'variable',
      scope: 'content',
      category: t('variables'),
      icon: 'pencil',
      command: ({ editor, range, addElement, slashScope }: CommandProps) => {
        const variableText = '{{  }}'
        const htmlContent = `<span class="variable-placeholder">${variableText}</span> `

        const rangeText = editor.state.doc.textBetween(range.from, range.to)
        const isSlashCommand = rangeText.startsWith('/') || shouldApplySlashInEditor(slashScope, editor)

        if (isSlashCommand) {
          // Delete range and insert variable text at cursor position
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContent(variableText + ' ')
            .run()

          // Find the inserted variable to apply the mark
          const { doc, selection } = editor.state
          const afterInsertPos = selection.from

          // Search backwards to find where the variable starts
          let variableStart = afterInsertPos
          for (let i = afterInsertPos; i >= Math.max(0, afterInsertPos - 50); i--) {
            if (doc.textBetween(Math.max(0, i - 3), i) === '{{ ') {
              variableStart = i - 3
              break
            }
          }

          const variableEnd = variableStart + variableText.length

          // Apply mark and position cursor
          editor
            .chain()
            .setTextSelection({ from: variableStart, to: variableEnd })
            .setMark('variablePlaceholder')
            .setTextSelection(variableStart + 3)
            .run()

          // Trigger suggestion plugin check
          requestAnimationFrame(() => {
            const { state, dispatch } = editor.view
            dispatch(state.tr.setSelection(state.selection))
          })
        } else {
          editor.chain().focus().deleteRange(range).run()
          addElement({ type: ElementType.message, textContent: htmlContent })
        }
      }
    }
  ] as SlashCommandDefinition[]
}

export function availableSlashCommands(
  query: { query: string; editor: Editor },
  parent: Vue,
  rootEntityType: RootEntityType | undefined = (parent as any).rootEntityType,
  slashScope?: EditorContext['slashScope'],
  executionContext?: SlashExecutionContext
): SlashCommand[] {
  const eligibility: SlashExecutionContext = executionContext ?? { slashCommands: true, slashScope }
  const isInTemplate = rootEntityType === RootEntityType.SPACE_PAGE_TEMPLATE
  const t = (key: string) => parent.$t(`slashCommands.${key}`)

  // position in the array matters and items of a group should always be next to each other
  const commands: SlashCommandDefinition[] = [
    ...textCommands(t),
    ...proposalCommands(t),
    ...progressCommands(t),
    ...offerCommands(t),
    ...mediaCommands(t),
    ...fileManagementCommands(t),
    ...layoutCommands(t),
    ...embedCommands(t),
    ...variableCommands(t)
  ]

  const isInTask = rootEntityType === RootEntityType.TASK || rootEntityType === RootEntityType.TASK_TEMPLATE

  return commands
    .map(item => ({
      ...item,
      title: item.title ?? (t(`${item.key}Title`) as string),
      subTitle: item.subTitle ?? (t(`${item.key}Subtitle`) as string)
    }))
    .filter(item => {
      if (!canExecuteSlashCommand(item, eligibility, { enforceElementHandler: !!executionContext }).ok) {
        return false
      }
      // In-editor HR divider is content-only; canvas keeps the Notch divider element.
      if (slashScope !== 'content' && item.key === 'horizontalRule') {
        return false
      }
      switch (item.title) {
        case t('taskListTitle'):
        case t('teamOverviewTitle'):
        case t('timelineTitle'):
        case t('welcomeTitle'):
        case t('clientLogosTitle'):
        case t('offerTitle'):
          if (isInTask) return false
          break
        case t('mentionTitle'):
          if (isInTemplate) return false
          break
        case t('signatureTitle'): {
          if (isInTask) {
            return false
          }
          break
        }
        default:
          break
      }

      const lowercaseQuery = query.query.toLowerCase()
      return (
        item.title.toLowerCase().includes(lowercaseQuery) ||
        item.keywords?.includes(lowercaseQuery) ||
        item.subTitle.toLowerCase().includes(lowercaseQuery) ||
        item.category.toLowerCase().includes(lowercaseQuery)
      )
    })
    .map((item, index) => {
      item.index = index
      return item
    })
}

export function replaceOrAddElement(props: CommandProps, data: AddElementData) {
  const { editor, range, replaceElement, addElement, slashScope } = props
  if (slashScope === 'content') {
    return
  }
  const { doc } = editor.state
  const slashText = slashRangeText(editor, range)
  if (doc.textContent.trim() === slashText.trim()) {
    replaceElement(data)
    return
  }

  // The block holds more than the slash line: keep what is above the caret, put the element right after it and
  // move what is below into a new text block behind the element
  const $from = doc.resolve(range.from)
  const $to = doc.resolve(range.to)
  // The space typed before the slash goes too: a trailing space does not survive the stored HTML, which breaks undo
  const textBefore = doc.textBetween($from.start(), range.from)
  let head =
    $from.parent.textContent === slashText
      ? $from.before()
      : range.from - (textBefore.length - textBefore.trimEnd().length)
  // Everything from head on is deleted, so a list item (or list) the slash line opens goes whole, not left empty
  for (let $head = doc.resolve(head); $head.depth > 0 && $head.pos === $head.start(); $head = doc.resolve(head)) {
    head = $head.before()
  }
  // Past the end of the slash line, and of any list item it closes, so the new block does not start empty
  let cutAt = $to.parentOffset === $to.parent.content.size ? $to.after() : range.to
  for (let $cut = doc.resolve(cutAt); $cut.depth > 0 && $cut.pos === $cut.end(); $cut = doc.resolve(cutAt)) {
    cutAt = $cut.after()
  }
  const tail = doc.cut(cutAt)
  const hasTail = tail.textBetween(0, tail.content.size, '\n', '￼').trim() !== ''
  editor.view.dispatch(editor.state.tr.deleteRange(head, doc.content.size))
  addElement(
    data,
    ...(hasTail
      ? [{ type: ElementType.message, textContent: generateHTML(tail.toJSON(), editor.extensionManager.extensions) }]
      : [])
  )
}
